import { AddInvoicedSupplier } from '../components/AddInvoicedSupplier';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardCheck, Loader2, Check, X, Package, ExternalLink, PoundSterling, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected']];

const ListingApprovals = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/listing-approvals`, { params: { status } }); setItems(data.items); setCounts(data.counts); }
    catch { setItems([]); } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action, reason, override) => {
    try {
      const url = `${API}/admin/listing-approvals/${id}/${action}${action === 'approve' && override ? '?override=true' : ''}`;
      await axios.post(url, action === 'reject' ? { reason } : {});
      toast.success(action === 'approve' ? (override ? 'Listing approved & live (payment overridden)' : 'Listing approved & live') : 'Sent back to supplier');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const markPaid = async (id) => {
    if (!window.confirm('Mark this supplier as paid (offline/invoiced)? The record will show Paid.')) return;
    try {
      await axios.post(`${API}/admin/listing-approvals/${id}/mark-paid`);
      toast.success('Marked as paid (offline)');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not mark as paid'); }
  };

  const reopen = async (a) => {
    if (status === 'approved' && !window.confirm(`Undo approval for ${a.supplier_name}? This takes the live listing back offline and returns it to Pending for re-review.`)) return;
    try {
      await axios.post(`${API}/admin/listing-approvals/${a.id}/reopen`, {});
      toast.success('Moved back to Pending for review');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reopen'); }
  };

  const approveClick = (a) => {
    if (a.paid) { act(a.id, 'approve'); return; }
    if (window.confirm(`${a.supplier_name} hasn't completed payment yet. Approve and take the listing live anyway?`)) {
      act(a.id, 'approve', null, true);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="listing-approvals">
      <div className="flex items-center justify-between gap-3 mb-1"><div className="flex items-center gap-2"><ClipboardCheck className="w-6 h-6 text-[#00A79D]" /><h1 className="font-heading text-2xl font-bold text-gray-900">Listing Approvals</h1></div><AddInvoicedSupplier /></div>
      <p className="text-gray-500 text-sm mb-5">Review supplier listings and add-on content, then approve to take them live.</p>

      <div className="flex gap-2 mb-5">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`approvals-tab-${k}`}
            className={`text-sm font-semibold px-4 py-2 rounded-md border ${status === k ? 'bg-[#00A79D] text-white border-[#00A79D]' : 'bg-white text-gray-600 border-gray-200'}`}>
            {label} {typeof counts[k] === 'number' && <span className="opacity-70">({counts[k]})</span>}
          </button>
        ))}
      </div>

      {loading ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>
        : items.length === 0 ? <div className="py-16 text-center text-gray-400" data-testid="approvals-empty">Nothing {status} right now.</div>
        : (
          <div className="space-y-4">
            {items.map((a) => {
              const p = a.listing_preview || {};
              const open = openId === a.id;
              return (
                <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`approval-${a.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-heading font-bold text-gray-900">{a.supplier_name}</h3>
                      <p className="text-sm text-gray-500">{a.contact_name} · {a.email}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {a.paid
                          ? <span className="text-[11px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded inline-flex items-center gap-1" data-testid={`approval-paid-${a.id}`}><PoundSterling className="w-3 h-3" />Paid{a.payment_method === 'offline' ? ' (offline)' : a.payment_method === 'invoice' ? ` by invoice ${a.invoice_number || ''}` : ''}{a.amount ? ` £${a.amount}` : ''}{a.plan ? ` · ${a.plan}` : ''}</span>
                          : <span className="text-[11px] font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded inline-flex items-center gap-1" data-testid={`approval-unpaid-${a.id}`}>Awaiting payment</span>}
                        {a.addon_labels?.map((l) => <span key={l} className="text-[11px] bg-[#00A79D]/10 text-[#00857D] px-2 py-0.5 rounded inline-flex items-center gap-1"><Package className="w-3 h-3" />{l}</span>)}
                      </div>
                      {status === 'approved' && (a.reviewed_by || a.reviewed_at) && (
                        <p className="text-[11px] text-gray-400 mt-1.5" data-testid={`approval-audit-${a.id}`}>
                          Approved{a.reviewed_by ? ` by ${a.reviewed_by}` : ''}{a.reviewed_at ? ` · ${new Date(a.reviewed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                          {a.payment_override ? ' · ⚠️ payment overridden' : (a.payment_method === 'offline' ? ' · paid offline' : '')}
                          {a.payment_method === 'invoice' && (a.term_months || a.featured_months || a.inclusions) ? <span className="block text-xs text-gray-600 mt-1 whitespace-pre-line" data-testid={`approval-inclusions-${a.id}`}>{`Package: ${a.term_months || 12} months${a.featured_months ? `, ${a.featured_months} month(s) featured from go-live` : ''}${a.inclusions ? `\n${a.inclusions}` : ''}`}</span> : null}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => setOpenId(open ? null : a.id)} className="text-sm text-gray-600 border border-gray-200 px-3 py-1.5 rounded-md" data-testid={`approval-review-${a.id}`}>{open ? 'Hide' : 'Review'}</button>
                      {status === 'pending' && <>
                        {!a.paid && <button onClick={() => markPaid(a.id)} className="inline-flex items-center gap-1 text-sm text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-md hover:bg-emerald-50" data-testid={`approval-mark-paid-${a.id}`}><PoundSterling className="w-4 h-4" /> Mark as paid</button>}
                        <button onClick={() => act(a.id, 'reject', 'Please add more detail')} className="inline-flex items-center gap-1 text-sm text-red-600 border border-red-200 px-3 py-1.5 rounded-md" data-testid={`approval-reject-${a.id}`}><X className="w-4 h-4" /> Request changes</button>
                        <button onClick={() => approveClick(a)} title={a.paid ? '' : 'Admin override: approve without completed payment'} className={`inline-flex items-center gap-1 text-sm text-white px-3 py-1.5 rounded-md ${a.paid ? 'bg-[#00A79D]' : 'bg-amber-600 hover:bg-amber-700'}`} data-testid={`approval-approve-${a.id}`}><Check className="w-4 h-4" /> {a.paid ? 'Approve & go live' : 'Approve anyway'}</button>
                      </>}
                      {status === 'approved' && <a href={`/suppliers/${a.supplier_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#00857D] border border-gray-200 px-3 py-1.5 rounded-md"><ExternalLink className="w-4 h-4" /> View live</a>}
                      {(status === 'rejected' || status === 'approved') && <button onClick={() => reopen(a)} className="inline-flex items-center gap-1 text-sm text-gray-700 border border-gray-200 px-3 py-1.5 rounded-md hover:bg-gray-50" data-testid={`approval-reopen-${a.id}`}><Undo2 className="w-4 h-4" /> Undo{status === 'rejected' ? '' : ' approval'}</button>}
                    </div>
                  </div>

                  {open && (
                    <div className="mt-4 border-t border-gray-100 pt-4 grid md:grid-cols-2 gap-5 text-sm">
                      <div>
                        <p className="font-semibold text-gray-700 mb-1">{p.tagline}</p>
                        <p className="text-gray-600 whitespace-pre-line">{p.description || 'No description yet.'}</p>
                        <div className="flex flex-wrap gap-1 mt-2">{(p.categories || []).map((c) => <span key={c} className="text-[11px] bg-gray-100 px-2 py-0.5 rounded">{c}</span>)}</div>
                        {(p.products || []).length > 0 && <div className="mt-3"><p className="font-semibold text-gray-700">Products</p><ul className="list-disc pl-5 text-gray-600">{p.products.map((pr, i) => <li key={i}><b>{pr.name}</b>{pr.description ? ` - ${pr.description}` : ''}</li>)}</ul></div>}
                      </div>
                      <div>
                        {Object.keys(a.addon_content || {}).length > 0 ? Object.entries(a.addon_content).map(([k, v]) => (
                          <div key={k} className="mb-3"><p className="font-semibold text-gray-700 capitalize">{k.replace(/_/g, ' ')}</p><p className="text-gray-600 whitespace-pre-line">{v || ' - '}</p></div>
                        )) : <p className="text-gray-400">No add-on content.</p>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
};

export default ListingApprovals;
